import ExcelJS from "exceljs";

/**
 * Converts a 1-based column number into an Excel column letter (1 -> A, 27 -> AA)
 */
export function getColumnLetter(colIndex) {
  let temp;
  let letter = "";
  let col = colIndex;
  while (col > 0) {
    temp = (col - 1) % 26;
    letter = String.fromCharCode(temp + 65) + letter;
    col = Math.floor((col - temp - 1) / 26);
  }
  return letter;
}

/**
 * Format date values to DD-MM-YYYY
 */
export function formatDateValue(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (isNaN(date.getTime())) return String(value);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
}

export function isDateField(colId = "") {
  const lower = String(colId).toLowerCase();
  return (
    lower.includes("date") ||
    lower.includes("created") ||
    lower.includes("updated") ||
    lower.includes("expiry")
  );
}

export function isNumericField(colId = "", value) {
  const lower = String(colId).toLowerCase();
  if (
    lower.includes("amount") ||
    lower.includes("value") ||
    lower.includes("price") ||
    lower.includes("cost") ||
    lower.includes("revenue") ||
    lower.includes("expense") ||
    lower.includes("profit") ||
    lower.includes("days")
  ) {
    return true;
  }
  return typeof value === "number";
}

function alignCellContent(cell, colId, value) {
  if (isDateField(colId)) {
    cell.alignment = { vertical: "middle", horizontal: "center" };
  } else if (isNumericField(colId, value)) {
    cell.alignment = { vertical: "middle", horizontal: "right" };
  } else if (
    String(colId).toLowerCase() === "status" ||
    String(colId).toLowerCase() === "id" ||
    String(colId).toLowerCase().includes("code")
  ) {
    cell.alignment = { vertical: "middle", horizontal: "center" };
  } else {
    cell.alignment = { vertical: "middle", horizontal: "left" };
  }
}

/**
 * Exports data to a beautifully formatted designer Excel file with:
 * - Frozen header row
 * - Native dropdown AutoFilter
 * - Corporate Navy Header styling
 * - Zebra striping on data rows
 * - Auto-fit column widths
 * - Smart data alignment
 */
export async function exportToDesignerExcel({
  data = [],
  columns = [],
  filename,
  sheetName = "CRM Data",
}) {
  const exportColumns = columns.filter((col) => col.id !== "select");
  if (exportColumns.length === 0) return;

  const workbook = new ExcelJS.Workbook();
  workbook.creator = "CRM Portal";
  workbook.created = new Date();

  const worksheet = workbook.addWorksheet(sheetName, {
    views: [{ state: "frozen", xSplit: 0, ySplit: 1, activeCell: "A2" }],
  });

  // 1. Column headers
  worksheet.columns = exportColumns.map((col) => ({
    header: typeof col.label === "string" ? col.label : col.id,
    key: col.id,
  }));

  // 2. Add data rows
  data.forEach((row) => {
    const rowValues = {};
    exportColumns.forEach((col) => {
      let val = col.exportValue ? col.exportValue(row) : row[col.id];
      if (isDateField(col.id)) {
        val = formatDateValue(val);
      }
      rowValues[col.id] = val !== undefined && val !== null ? val : "-";
    });
    worksheet.addRow(rowValues);
  });

  // 3. AutoFilter
  const lastColLetter = getColumnLetter(exportColumns.length);
  const totalRows = data.length + 1; // +1 for header row
  worksheet.autoFilter = `A1:${lastColLetter}${Math.max(totalRows, 2)}`;

  // 4. Header Styling
  const headerRow = worksheet.getRow(1);
  headerRow.height = 28;
  headerRow.eachCell((cell) => {
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF1E3A8A" }, // Deep Corporate Navy Blue
    };
    cell.font = {
      name: "Calibri",
      size: 11,
      bold: true,
      color: { argb: "FFFFFFFF" },
    };
    cell.alignment = {
      vertical: "middle",
      horizontal: "left",
      wrapText: true,
    };
    cell.border = {
      top: { style: "thin", color: { argb: "FF1E293B" } },
      bottom: { style: "medium", color: { argb: "FF0F172A" } },
      left: { style: "thin", color: { argb: "FF2563EB" } },
      right: { style: "thin", color: { argb: "FF2563EB" } },
    };
  });

  // 5. Data Rows Styling (Zebra striping, borders, alignments)
  for (let r = 2; r <= totalRows; r++) {
    const row = worksheet.getRow(r);
    row.height = 22;
    const isEven = r % 2 === 0;
    const bgColor = isEven ? "FFFFFFFF" : "FFF8FAFC"; // White & Soft Slate Tint

    row.eachCell({ includeEmpty: true }, (cell, colIndex) => {
      const colDef = exportColumns[colIndex - 1];
      const colId = colDef ? colDef.id : "";

      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: bgColor },
      };

      cell.border = {
        top: { style: "thin", color: { argb: "FFE2E8F0" } },
        bottom: { style: "thin", color: { argb: "FFE2E8F0" } },
        left: { style: "thin", color: { argb: "FFF1F5F9" } },
        right: { style: "thin", color: { argb: "FFF1F5F9" } },
      };

      cell.font = {
        name: "Calibri",
        size: 10,
        color: { argb: "FF334155" },
      };

      alignCellContent(cell, colId, cell.value);
    });
  }

  // 6. Auto-fit Column Widths
  worksheet.columns.forEach((column) => {
    let maxLength = column.header ? String(column.header).length : 12;
    column.eachCell({ includeEmpty: false }, (cell) => {
      const valStr = cell.value != null ? String(cell.value) : "";
      if (valStr.length > maxLength) {
        maxLength = valStr.length;
      }
    });
    // Add padding and constrain min/max
    column.width = Math.min(Math.max(maxLength + 4, 14), 50);
  });

  // 7. Write to Buffer and Trigger Browser Download
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || `export_${Date.now()}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}
